import React from "react";
import { NavLink } from "react-router-dom";
import "../styles/Advisory.css";

export default function Advisory() {
  const advisoryServices = [
    {
      number: "01",
      stage: "Immediate Need",
      title: "OHS Support That Delivers Sustainable Change",
      description:
        "Resolve pressing occupational health and safety challenges through focused, evidence-informed support.",
      details:
        "GESI can assist with incident response, hazard and risk assessment, psychological health considerations, program development, training needs, and other defined OHS priorities.",
      outcome:
        "The objective is not only to address the immediate issue, but to identify the system conditions influencing it and strengthen the organization’s ability to prevent recurrence.",
      areas: [
        "Incident response",
        "Hazard and risk assessment",
        "Psychological health considerations",
        "Program and training needs",
      ],
      cta: "Request OHS Support",
      link: "/contact",
    },
    {
      number: "02",
      stage: "System Insight",
      title: "Audit, Gap Analysis and Governance Review",
      description:
        "Evaluate how effectively safety is designed, governed, implemented, and measured.",
      details:
        "Reviews may reference recognized frameworks and, where appropriate, the developing PRIME360™ model.",
      outcome:
        "The result is a practical roadmap focused on accountability, risk control, cultural alignment, and continual improvement.",
      areas: [
        "Governance and accountability",
        "Program and system design",
        "Implementation effectiveness",
        "Measurement and improvement",
      ],
      cta: "Discuss a System Review",
      link: "/contact",
    },
    {
      number: "03",
      stage: "Organizational Capability",
      title: "Executive and Board-Level Safety Governance",
      description:
        "Strengthen the connection between occupational health and safety, enterprise risk, strategy, and organizational oversight.",
      details:
        "Clarify accountability, improve the quality of information reaching leadership, and integrate prevention into business decisions.",
      outcome:
        "GESI helps leaders move from reactive oversight toward informed governance that supports stronger decisions and lasting organizational performance.",
      areas: [
        "Executive accountability",
        "Board-level oversight",
        "Enterprise risk alignment",
        "Strategic decision support",
      ],
      cta: "Discuss Governance Advisory",
      link: "/contact",
    },
  ];

  return (
    <main className="advisory-page">
      {/* ADVISORY PAGE HERO */}
      <section
        className="advisory-hero"
        aria-labelledby="advisory-hero-title"
      >
        <div className="advisory-hero-background" aria-hidden="true">
          <span className="advisory-hero-glow advisory-hero-glow-left"></span>
          <span className="advisory-hero-glow advisory-hero-glow-right"></span>
          <span className="advisory-hero-orbit advisory-hero-orbit-one"></span>
          <span className="advisory-hero-orbit advisory-hero-orbit-two"></span>
        </div>

        <div className="advisory-hero-container">
          <p className="advisory-kicker">
            Strategic Advisory
          </p>

          <h1 id="advisory-hero-title" className="advisory-hero-title">
            Strategic Advisory for
            <span> Safer, Stronger Organizations</span>
          </h1>

          <p className="advisory-hero-description">
            GESI helps leaders address immediate occupational health and safety
            challenges while strengthening the systems, governance, and
            organizational capability required for lasting improvement.
          </p>

          <NavLink
            to="/contact"
            className="advisory-primary-button"
          >
            <span>Request OHS Support</span>

            <span
              className="advisory-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </NavLink>

          <div
            className="advisory-hero-path"
            aria-label="Advisory progression"
          >
            <span>Immediate Support</span>
            <span aria-hidden="true">→</span>
            <span>System Insight</span>
            <span aria-hidden="true">→</span>
            <span>Organizational Capability</span>
          </div>
        </div>
      </section>

      {/* ADVISORY INTRODUCTION */}
      <section
        className="advisory-introduction"
        aria-labelledby="advisory-introduction-title"
      >
        <div className="advisory-introduction-container">
          <div className="advisory-introduction-heading">
            <p className="advisory-section-kicker">
              Connected Advisory
            </p>

            <h2
              id="advisory-introduction-title"
              className="advisory-section-title"
            >
              Move Beyond Isolated
              <span> Safety Solutions</span>
            </h2>
          </div>

          <div className="advisory-introduction-copy">
            <p>
              Safety issues rarely exist in isolation. They are shaped by
              leadership decisions, operational design, culture, workforce
              capability, data, and accountability.
            </p>

            <p>
              GESI provides focused technical support and governance-led
              advisory services that connect these factors and establish
              practical priorities for action.
            </p>
          </div>

          <div
            className="advisory-factors"
            aria-label="Factors connected through GESI advisory"
          >
            <span>Leadership</span>
            <span>Operations</span>
            <span>Culture</span>
            <span>Capability</span>
            <span>Evidence</span>
            <span>Accountability</span>
          </div>
        </div>
      </section>

      {/* ADVISORY SERVICE PROGRESSION */}
      <section
        className="advisory-services"
        aria-labelledby="advisory-services-title"
      >
        <div className="advisory-services-background" aria-hidden="true"></div>

        <div className="advisory-services-container">
          <div className="advisory-services-heading">
            <p className="advisory-section-kicker">
              Advisory Progression
            </p>

            <h2
              id="advisory-services-title"
              className="advisory-section-title"
            >
              From Immediate Need to
              <span> Organizational Capability</span>
            </h2>

            <p className="advisory-services-intro">
              Each level of support builds on the last, helping organizations
              resolve defined challenges, understand system conditions, and
              strengthen long-term leadership capability.
            </p>
          </div>

          <div className="advisory-progression">
            <div
              className="advisory-progression-line"
              aria-hidden="true"
            ></div>

            {advisoryServices.map((service, index) => (
              <article
                className="advisory-service"
                key={service.title}
              >
                <div className="advisory-service-marker">
                  <span>{service.number}</span>
                </div>

                <div className="advisory-service-card">
                  <div className="advisory-service-top">
                    <div>
                      <p className="advisory-service-stage">
                        {service.stage}
                      </p>

                      <h3>{service.title}</h3>
                    </div>

                    <span
                      className="advisory-service-index"
                      aria-hidden="true"
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="advisory-service-body">
                    <div className="advisory-service-copy">
                      <p className="advisory-service-lead">
                        {service.description}
                      </p>

                      <p>{service.details}</p>

                      <div className="advisory-service-outcome">
                        <span aria-hidden="true">✓</span>
                        <p>{service.outcome}</p>
                      </div>
                    </div>

                    <div className="advisory-service-side">
                      <p className="advisory-service-areas-label">
                        Areas of support
                      </p>

                      <ul className="advisory-service-areas">
                        {service.areas.map((area) => (
                          <li key={area}>
                            <span aria-hidden="true"></span>
                            {area}
                          </li>
                        ))}
                      </ul>

                      <NavLink
                        to={service.link}
                        className="advisory-service-button"
                      >
                        <span>{service.cta}</span>

                        <span
                          className="advisory-service-button-arrow"
                          aria-hidden="true"
                        >
                          ↗
                        </span>
                      </NavLink>
                    </div>
                  </div>

                  <span
                    className="advisory-service-bottom-line"
                    aria-hidden="true"
                  ></span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ADVISORY FINAL CTA */}
      <section
        className="advisory-final-cta"
        aria-labelledby="advisory-final-title"
      >
        <div className="advisory-final-background" aria-hidden="true">
          <span className="advisory-final-glow"></span>
        </div>

        <div className="advisory-final-container">
          <p className="advisory-section-kicker">
            Begin with the Current Need
          </p>

          <h2
            id="advisory-final-title"
            className="advisory-final-title"
          >
            Strengthen the System
            <span> Behind Safety Performance.</span>
          </h2>

          <p className="advisory-final-description">
            Start with the issue your organization needs to address today.
            GESI will help clarify the challenge, identify practical priorities,
            and build a path toward stronger long-term capability.
          </p>

          <NavLink
            to="/contact"
            className="advisory-primary-button"
          >
            <span>Start a Conversation</span>

            <span
              className="advisory-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </NavLink>
        </div>
      </section>
    </main>
  );
}